/**
 * Attendance Status Sync Service
 *
 * This service syncs the current attendance status to Capacitor Preferences
 * so the Background Runner can read it to determine if notifications should be cancelled.
 *
 * Called whenever the user checks in or checks out.
 */

import { Preferences } from '@capacitor/preferences'

interface AttendanceStatus {
    hasCheckedIn: boolean
    hasCheckedOut: boolean
    checkInTime?: string
    checkOutTime?: string
}

/**
 * Update attendance status for today
 * @param hasCheckedIn Whether user has checked in today
 * @param hasCheckedOut Whether user has checked out today
 * @param checkInTime Optional check-in timestamp
 * @param checkOutTime Optional check-out timestamp
 */
export async function syncAttendanceStatus(
    hasCheckedIn: boolean,
    hasCheckedOut: boolean,
    checkInTime?: string,
    checkOutTime?: string
): Promise<void> {
    try {
        // Get today's date string (YYYY-MM-DD)
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        const status: AttendanceStatus = {
            hasCheckedIn,
            hasCheckedOut,
            checkInTime,
            checkOutTime
        }

        // Store status with today's date as key
        await Preferences.set({
            key: `attendance_status_${todayStr}`,
            value: JSON.stringify(status)
        })

        console.log('[AttendanceStatusSync] Updated status for', todayStr, status)
    } catch (error) {
        console.error('[AttendanceStatusSync] Failed to sync status:', error)
    }
}

/**
 * Clear attendance status for today
 * Called at midnight or when resetting
 */
export async function clearTodayAttendanceStatus(): Promise<void> {
    try {
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        await Preferences.remove({
            key: `attendance_status_${todayStr}`
        })

        console.log('[AttendanceStatusSync] Cleared status for', todayStr)
    } catch (error) {
        console.error('[AttendanceStatusSync] Failed to clear status:', error)
    }
}

/**
 * Get current attendance status for today
 */
export async function getTodayAttendanceStatus(): Promise<AttendanceStatus | null> {
    try {
        const today = new Date()
        const todayStr = today.toISOString().split('T')[0]

        const { value } = await Preferences.get({
            key: `attendance_status_${todayStr}`
        })

        if (value) {
            return JSON.parse(value) as AttendanceStatus
        }

        return null
    } catch (error) {
        console.error('[AttendanceStatusSync] Failed to get status:', error)
        return null
    }
}

/**
 * Clean up old attendance status entries (older than 7 days)
 * Call this periodically to prevent storage bloat
 */
export async function cleanupOldAttendanceStatus(): Promise<void> {
    try {
        const keys = await Preferences.keys()
        const attendanceKeys = keys.keys.filter(key => key.startsWith('attendance_status_'))

        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
        const cutoffStr = sevenDaysAgo.toISOString().split('T')[0]

        for (const key of attendanceKeys) {
            // Extract date from key (format: attendance_status_YYYY-MM-DD)
            const dateStr = key.replace('attendance_status_', '')

            if (dateStr < cutoffStr) {
                await Preferences.remove({ key })
                console.log('[AttendanceStatusSync] Removed old status:', key)
            }
        }
    } catch (error) {
        console.error('[AttendanceStatusSync] Failed to cleanup old status:', error)
    }
}
